import { useTranslation } from 'react-i18next';

/**
 * 축 넷 레이더(시안 §8.3 `921:5549`) — 300×300, 마름모 격자 3겹, 축 끝에 이름과 값.
 *
 * **값이 있는 축에는 점을 찍고, 다각형은 넷 다 있을 때만 그린다.** 다각형은 네 꼭짓점을
 * 이어야 모양이 되므로 셋뿐인 채로 그리면 없는 축을 0으로 이어 버린다. 점은 그런 문제가
 * 없다 — 잰 축만 찍힌다. 축이 차는 동안 화면이 채워지는 게 보이고, 넷째가 차는 날 다각형이
 * 처음 선다.
 *
 * 값이 없는 축의 값 자리엔 `—`가 선다(0으로 찍으면 안 잰 것이 "0점"이 된다). 점이 하나도
 * 없을 때만 가운데에 빈 상태 문구를 낸다.
 *
 * `average`는 시안의 점선 다각형이다. 비교할 평균이 없으면 안 그린다 — 지어낸 기준선은
 * 내 값을 그것에 견주게 만든다.
 */

const SIZE = 300;
const CENTER = SIZE / 2;
/** 축 끝 라벨 자리를 빼고 남는 반지름. */
const RADIUS = 105;
const RINGS = [1 / 3, 2 / 3, 1];
/**
 * 좌우 라벨 여백. 좌우 라벨은 축 끝에서 **바깥으로** 자라는데(`LABEL_POS`) 300 캔버스에는
 * 12px밖에 안 남는다 — 시안의 두 글자(`난조`·`보통`)는 들어가도 실제 문구 `많이 잠김`·
 * `Typical`은 46px 안팎이라 SVG 경계에서 잘렸다(402px 뷰포트 실측, 좌 라벨 13px 잘림).
 * 레이더 자체의 크기·위치는 시안 그대로 두고 캔버스만 양옆으로 넓힌다.
 */
const SIDE_PAD = 32;

/** 축 순서 = 위 · 오른쪽 · 아래 · 왼쪽(시계 방향). 시안의 최상·보통·살짝 잠김·난조(화면 문구는 '많이 잠김' — #60). */
const ANGLES = [-90, 0, 90, 180].map((deg) => (deg * Math.PI) / 180);

const pointAt = (index: number, ratio: number): [number, number] => [
  CENTER + Math.cos(ANGLES[index]) * RADIUS * ratio,
  CENTER + Math.sin(ANGLES[index]) * RADIUS * ratio,
];

const polygon = (ratios: number[]): string =>
  ratios
    .map((ratio, i) =>
      pointAt(i, ratio)
        .map((v) => v.toFixed(1))
        .join(','),
    )
    .join(' ');

export interface RadarAxis {
  id: string;
  label: string;
  /** 0..100. null = 못 쟀다. */
  value: number | null;
}

/** 라벨 자리 — 축 끝에서 바깥으로. 위·아래는 가운데 정렬, 좌우는 축 쪽으로 붙는다. */
const LABEL_POS: {
  x: number;
  y: number;
  anchor: 'middle' | 'start' | 'end';
}[] = [
  { x: CENTER, y: CENTER - RADIUS - 24, anchor: 'middle' },
  { x: CENTER + RADIUS + 12, y: CENTER - 4, anchor: 'start' },
  { x: CENTER, y: CENTER + RADIUS + 22, anchor: 'middle' },
  { x: CENTER - RADIUS - 12, y: CENTER - 4, anchor: 'end' },
];

export const RadarChart = ({
  axes,
  average,
  label,
}: {
  /** 정확히 넷 — 시안의 축 수다. */
  axes: RadarAxis[];
  /** 축과 같은 순서의 평균(0..100). 없으면 점선을 안 그린다. */
  average?: number[];
  /** 접근성 이름. */
  label: string;
}) => {
  const { t } = useTranslation();
  const filled = axes.filter((axis) => axis.value !== null);
  const complete = filled.length === axes.length;
  const mine = complete
    ? polygon(axes.map((axis) => (axis.value as number) / 100))
    : null;
  const avg =
    average && average.length === axes.length
      ? polygon(average.map((v) => v / 100))
      : null;

  return (
    <svg
      viewBox={`${-SIDE_PAD} 0 ${SIZE + SIDE_PAD * 2} ${SIZE}`}
      // 좁은 화면에서는 폭에 맞춰 줄어든다 — 고정 폭이면 라벨 여백이 화면 밖으로 밀린다.
      className="mx-auto h-auto w-full max-w-[364px]"
      role="img"
      aria-label={label}
    >
      {RINGS.map((ring) => (
        <polygon
          key={ring}
          points={polygon(axes.map(() => ring))}
          fill="none"
          stroke="var(--bk-200)"
          strokeWidth={1}
        />
      ))}
      {axes.map((_, i) => {
        const [x, y] = pointAt(i, 1);
        return (
          <line
            key={i}
            x1={CENTER}
            y1={CENTER}
            x2={x}
            y2={y}
            stroke="var(--bk-200)"
            strokeWidth={1}
          />
        );
      })}

      {avg && (
        <polygon
          data-testid="radar-average"
          points={avg}
          fill="none"
          stroke="var(--bk-400)"
          strokeWidth={1.5}
          strokeDasharray="4 4"
        />
      )}
      {mine && (
        <polygon
          data-testid="radar-mine"
          points={mine}
          fill="hsl(var(--primary) / 0.12)"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}
      {axes.map((axis, i) =>
        axis.value === null ? null : (
          <circle
            key={axis.id}
            data-testid="radar-dot"
            cx={pointAt(i, axis.value / 100)[0]}
            cy={pointAt(i, axis.value / 100)[1]}
            r={3.5}
            fill="hsl(var(--primary))"
          />
        ),
      )}
      {filled.length === 0 && (
        <text
          x={CENTER}
          y={CENTER + 4}
          textAnchor="middle"
          fontSize={12}
          fill="var(--bk-500)"
        >
          {t('web.result.pending')}
        </text>
      )}

      {axes.map((axis, i) => {
        const pos = LABEL_POS[i];
        return (
          <g key={axis.id} textAnchor={pos.anchor}>
            <text
              x={pos.x}
              y={pos.y}
              fontSize={12.5}
              fontWeight={500}
              fill="var(--bk-600)"
            >
              {axis.label}
            </text>
            <text
              x={pos.x}
              y={pos.y + 18}
              fontSize={15}
              fontWeight={700}
              fill="var(--bk-900)"
            >
              {axis.value === null ? '—' : Math.round(axis.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
